import React, { FC, useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "../../../routing";

import {
  InlineResponse400Errors,
  ResourceWithoutId,
  ResourcePropertiesResourceTypeEnum,
  ResourceProperties,
  CustomFieldUpdate,
} from "../../../../bookmydesk-api-sdk-typescript-axios";
import styled from "styled-components/native";
import { t } from "../../../i18n";

import Button from "../../../components/Button";
import Container from "../../../components/Container";
import LabeledInput, {
  InputWrapper,
  LabeledInputWrapper,
  LabelWrapper,
} from "../../../components/LabeledInput";
import LoadOverlay from "../../../components/LoadOverlay";
import Select, { SelectItemInterface } from "../../../components/Select";

import { color, spacing, wpdColor } from "../../../styles/theme";
import { FormCard } from "../../../styles/Card";
import Title from "../../../styles/Title";
import Input, { InputHelpText } from "../../../styles/Input";

import { useAuth } from "../../../context/authContext";
import { useApi, InputValidationError } from "../../../hooks/useApi";
import { useClient } from "../../../hooks/useClient";
import InputValidationErrors from "../../../components/InputValidationErrors";
import Checkbox from "../../../components/Checkbox";
import { CustomFields } from "../../../components/CustomFields";

const StyledFormCard = styled(FormCard)`
  margin: ${spacing.large} auto;
`;

export const ResourceUpdate: FC = () => {
  const [errors, setErrors] = useState<InlineResponse400Errors[]>([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [customFields, setCustomFields] = useState<CustomFieldUpdate[]>([]);

  const [locationId, setLocationId] = useState("");

  const [locations, setLocations] = useState<SelectItemInterface[]>([]);
  const [resourceType, setResourceType] =
    useState<ResourcePropertiesResourceTypeEnum>(
      ResourcePropertiesResourceTypeEnum.Parkingspot
    );
  const [resourceTypes, setResourceTypes] = useState<SelectItemInterface[]>([]);
  const [reservationConnectionEnabled, setReservationConnectionEnabled] =
    useState<boolean>(false);
  const [active, setActive] = useState<boolean>(false);
  const [amountAvailable, setAmountAvailable] = useState<string>("0");

  const { userState } = useAuth();
  const client = useClient();
  const { handleRequest, isLoading, error, dismissError } = useApi();
  const history = useHistory();

  const userCompany = userState?.companyIds[0];

  const { id } = useParams();

  useEffect(() => {
    if (userCompany) {
      setResourceTypes([
        {
          label: t("admin.parking.create.form.parkingSpot"),
          value: ResourcePropertiesResourceTypeEnum.Parkingspot,
          isActive:
            resourceType === ResourcePropertiesResourceTypeEnum.Parkingspot,
        },
        {
          label: t("admin.parking.create.form.equipment"),
          value: ResourcePropertiesResourceTypeEnum.Equipment,
          isActive:
            resourceType === ResourcePropertiesResourceTypeEnum.Equipment,
        },
        {
          label: t("admin.parking.create.form.food"),
          value: ResourcePropertiesResourceTypeEnum.Food,
          isActive: resourceType === ResourcePropertiesResourceTypeEnum.Food,
        },
      ]);

      const fetchData = async () => {
        const resourceResponse = await handleRequest(client.getResource(id));
        if (!resourceResponse || !resourceResponse.data.result) {
          return;
        }

        const locationResponse = await handleRequest(
          client.listLocations(userCompany)
        );
        if (!locationResponse || !locationResponse.data.result) {
          return;
        }

        const locationResponseItems = locationResponse.data.result.items;
        const {
          name,
          locationId,
          description,
          active,
          amountAvailable,
          reservationConnectionEnabled,
          resourceType,
          customFields,
        } = resourceResponse.data.result.resource;

        const locationItems = locationResponseItems.map((location) => ({
          label: location.name,
          value: location.id,
          isActive: location.id === locationId,
        }));

        setLocations(locationItems);
        setLocationId(locationId);
        setName(name);
        setResourceType(resourceType);
        setCustomFields(customFields);
        setDescription(description || "");
        setActive(active || false);
        setAmountAvailable(`${amountAvailable}`);
        setReservationConnectionEnabled(reservationConnectionEnabled);
      };

      fetchData();
    }
  }, [client, userCompany, handleRequest, id, setName]);

  useEffect(() => {
    if (!userCompany) {
      return;
    }
    const fetchLocations = async () =>
      await handleRequest(client.listLocations(userCompany));

    fetchLocations().then((result) => {
      if (!result || !result.data.result) {
        return;
      }

      const { items } = result.data.result;
      setLocations(
        items.map((item) => ({
          label: item.name,
          value: item.id,
          isActive: item.id === locationId,
        }))
      );
    });
  }, [userCompany, client, handleRequest, locationId]);

  const updateLocation = useCallback(
    (newValue) => {
      setLocationId(newValue[0].value);
    },
    [setLocationId]
  );

  const updateResourceType = useCallback(
    (newValue) => {
      setResourceType(newValue[0].value);
    },
    [setResourceType]
  );

  const formIsFilledIn = useCallback(() => {
    return !!name && !!locationId && !isNaN(parseInt(amountAvailable));
  }, [name, locationId, amountAvailable]);

  const submitForm = useCallback(async () => {
    const data: ResourceProperties = {
      name,
      description,
      active,
      locationId,
      resourceType,
      reservationConnectionEnabled,
      amountAvailable: parseInt(amountAvailable),
      customFields: customFields.map((customField) => ({
        ...customField,
        id: customField.id?.startsWith("__new__") ? null : customField.id,
      })),
    };
    try {
      const success = await handleRequest(client.updateResource(id, data));
      if (success) {
        history.goBack();
      }
    } catch (error) {
      console.error(error);
      if (error instanceof InputValidationError) {
        const theError = error as InputValidationError;
        setErrors(theError.errors);
      }
    }
  }, [
    name,
    description,
    active,
    locationId,
    resourceType,
    reservationConnectionEnabled,
    amountAvailable,
    customFields,
    handleRequest,
    client,
    id,
    history,
  ]);

  return (
    <>
      {isLoading ? <LoadOverlay /> : null}
      <Container>
        <StyledFormCard>
          {Boolean(error) && (
            <InputValidationErrors
              errors={errors}
              closeNotification={dismissError}
            />
          )}
          <Title hasMarginBottom>
            {t("admin.parking.create.form.addTitle")}
          </Title>
          <LabeledInput label={t("admin.parking.create.form.name")} required>
            <Input
              onChangeText={setName}
              value={name}
              placeholder={t("admin.parking.create.form.name") + " *"}
            />
          </LabeledInput>
          <LabeledInput
            label={t("admin.parking.create.form.description")}
            required
          >
            <Input
              onChangeText={setDescription}
              value={description}
              placeholder={t("admin.parking.create.form.description") + " *"}
            />
          </LabeledInput>
          <LabeledInput
            label={t("admin.parking.create.form.location")}
            style={{ zIndex: 3 }}
            required
          >
            <Select
              required
              isMultiSelect={false}
              items={locations}
              onChange={updateLocation}
              placeholder={t("admin.parking.create.form.location")}
            />
          </LabeledInput>

          {/* <LabeledInput
            label={t("admin.parking.create.form.resourceType")}
            style={{ zIndex: 3}}
            required
          >
            <Select
              required
              isMultiSelect={false}
              items={resourceTypes}
              onChange={updateResourceType}
              placeholder={t("admin.parking.create.form.resourceType")}
            />
          </LabeledInput> */}

          <LabeledInputWrapper>
            <LabelWrapper />

            <InputWrapper>
              <Checkbox
                label={t(
                  "admin.parking.create.form.reservationConnectionEnabled"
                )}
                onChange={setReservationConnectionEnabled}
                checked={reservationConnectionEnabled}
              />
              <InputHelpText>
                {t("admin.parking.create.form.reservationConnectionEnabled")}
              </InputHelpText>
            </InputWrapper>
          </LabeledInputWrapper>

          <LabeledInputWrapper>
            <LabelWrapper />

            <InputWrapper>
              <Checkbox
                label={t("admin.parking.create.form.active")}
                onChange={setActive}
                checked={active}
              />
              <InputHelpText>
                {t("admin.parking.create.form.active")}
              </InputHelpText>
            </InputWrapper>
          </LabeledInputWrapper>

          <LabeledInput
            label={t("admin.parking.create.form.amountAvailable")}
            required
          >
            <Input
              onChangeText={(text) => setAmountAvailable(text)}
              value={amountAvailable}
              placeholder={
                t("admin.parking.create.form.amountAvailable") + " *"
              }
            />
          </LabeledInput>

          <CustomFields data={customFields} onChange={setCustomFields} />

          <Button
            hasMarginBottom
            disabled={!formIsFilledIn()}
            onPress={submitForm}
          >
            {t("admin.parking.create.form.submitUpdate")}
          </Button>
          <Button
            backgroundColor="transparent"
            backgroundHoverColor={color.primary}
            borderColor={color.primary}
            color={color.primary}
            textHoverColor={wpdColor.backgroundLighter}
            onPress={() => history.goBack()}
          >
            {t("general.cancel")}
          </Button>
        </StyledFormCard>
      </Container>
    </>
  );
};
